import React, { useLayoutEffect, useRef } from 'react';
import ChangedDot from './ChangedDot';
import { sectionDomId, useSheetContext } from './context';
import './sheet.css';

/**
 * Μία ενότητα του φύλλου: αριστερά (30%) τίτλος με τη μικρή μπάρα στο χρώμα της εφαρμογής και μία
 * γραμμή εξήγησης, δεξιά οι ρυθμίσεις· λεπτή γραμμή από πάνω. Σε στενό χώρο το ένα κάτω από το άλλο.
 * Δηλώνεται στο ευρετήριο του SettingsSheet (αν υπάρχει)· id στο DOM: settings-<id>.
 */
export interface SheetSectionProps {
  id: string;
  title: string;
  /** Μία γραμμή κάτω από τον τίτλο· μπορεί να έχει και σύνδεσμο. */
  hint?: React.ReactNode;
  /** Έχει αλλαγές που δεν αποθηκεύτηκαν: τελεία στο ευρετήριο και στον τίτλο. */
  dirty?: boolean;
  /** Σύντομο όνομα στο ευρετήριο (αλλιώς ο τίτλος). */
  indexLabel?: string;
  /** Κάτω από την εξήγηση, στην αριστερή στήλη (π.χ. «Προεπιλογή εφαρμογής», «Προσθήκη σειράς»). */
  aside?: React.ReactNode;
  testId?: string;
  children: React.ReactNode;
}

export const SheetSection: React.FC<SheetSectionProps> = ({ id, title, hint, dirty = false, indexLabel, aside, testId, children }) => {
  const sheet = useSheetContext();
  const ref = useRef<HTMLElement>(null);
  const domId = sectionDomId(id);
  const label = indexLabel || title;

  useLayoutEffect(() => {
    sheet?.register({ id, label, dirty, el: ref.current });
  }, [sheet, id, label, dirty]);
  useLayoutEffect(() => () => sheet?.unregister(id), [sheet, id]);

  return (
    <section
      ref={ref}
      id={domId}
      className="xs-sec"
      aria-labelledby={`${domId}-title`}
      data-sheet-section={id}
      data-testid={testId}
    >
      <div>
        <h2 id={`${domId}-title`} className="xs-sec-title" tabIndex={-1} data-sheet-heading="">
          <span>
            {title}
            {dirty && <ChangedDot />}
          </span>
        </h2>
        {hint && <p className="xs-sec-hint">{hint}</p>}
        {aside && <div className="xs-sec-aside">{aside}</div>}
      </div>
      <div className="xs-sec-body">{children}</div>
    </section>
  );
};

/** Πλήρους πλάτους κομμάτι του φύλλου (φόρτωση, σφάλμα, σημείωση), με την ίδια γραμμή από πάνω. */
export const SheetBlock: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`xs-block ${className}`.trim()}>{children}</div>
);

export default SheetSection;
